import Head from 'next/head';
import SiteLayout from '@/components/site/SiteLayout';
import {
  ActionLink,
  CardGrid,
  CtaPanel,
  PageHero,
  SectionBlock,
} from '@/components/site/PageSections';
import { getAbsoluteUrl, SITE_URL } from '@/lib/seoRoutes';

const ResourcesPage = () => {
  return (
    <SiteLayout
      title='Radiology Operations Resources | Contrast Coverage Insights'
      description='Read resources for imaging center operators on contrast coverage, virtual supervision, onsite staffing, compliance, and radiology operations strategy.'
      keywords={[
        'radiology operations resources',
        'contrast coverage insights',
        'imaging center staffing',
        'virtual supervision',
      ]}
    >
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: 'Radiology Operations Resources',
              url: getAbsoluteUrl('/resources'),
              description:
                'Resources for imaging center operators on contrast coverage, virtual supervision, onsite staffing, compliance, and radiology operations strategy.',
              publisher: {
                '@type': 'Organization',
                name: 'DocMoonlight',
                url: SITE_URL,
              },
            }),
          }}
        />
      </Head>
      <PageHero
        eyebrow='Resources'
        title='Insights for Imaging Centers & Modern Radiology Operations'
        description='Running an imaging center today takes more than clinical expertise. It requires efficient operations, smart staffing, and a clear handle on compliance. DocMoonlight Insights is built for the people actually running imaging centers every day.'
        actions={[
          { href: '#resource-categories', label: 'Explore Resources' },
          { href: '/about', label: 'Why DocMoonlight', variant: 'secondary' },
        ]}
        aside={
          <div className='rounded-[1.5rem] bg-slate-50 p-5'>
            <div className='text-sm font-bold uppercase tracking-[0.22em] text-slate-500'>
              Focus areas
            </div>
            <p className='mt-3 text-lg leading-8 text-slate-700'>
              Practical perspectives on contrast coverage, staffing strategies,
              and the shift between virtual and onsite care.
            </p>
          </div>
        }
      />

      <SectionBlock title='Resource Categories' id='resource-categories'>
        <CardGrid
          items={[
            {
              title: 'Blogs',
              body: 'Practical writing for imaging leaders on compliance, coverage strategy, and operational efficiency.',
            },
            {
              title: 'In The News',
              body: 'Updates, announcements, and relevant coverage tied to DocMoonlight and the broader imaging landscape.',
            },
          ]}
        />
      </SectionBlock>

      <SectionBlock title='What to Expect'>
        <p>
          This section is now positioned as a clear entry point for thought
          leadership instead of an afterthought. The structure is ready for
          ongoing blog and media updates as content is published.
        </p>
        <div className='mt-6'>
          <ActionLink href='/news' label='Visit In The News' variant='secondary' />
        </div>
      </SectionBlock>

      <CtaPanel
        title='Need a Coverage Strategy Instead of More Reading?'
        body='DocMoonlight can help you build an onsite, virtual, or hybrid model tailored to your imaging center.'
        action={{ href: '/hybrid-coverage#coverage-form', label: 'Talk to DocMoonlight' }}
      />
    </SiteLayout>
  );
};

export default ResourcesPage;
